import { useState, useEffect } from 'react';
import Kendra from 'aws-sdk/clients/kendra';
import { AvailableFacetManager } from '../search/facets/AvailableFacetManager';
import { SelectedFacetManager } from '../search/facets/SelectedFacetManager';
import {
    DataSourceNameLookup,
    getAttributeTypeLookup,
    getDataSourceNameLookup,
    IndexFieldNameToDocumentAttributeValueType,
} from '../search/facets/utils';
import { QueryResultType, QuerySuggestionsMode, PAGE_SIZE } from '../constants';
import { useApi } from 'src/hooks/useApi';

interface UseKendraSearchProps {
    initialQuery?: string;
    userLanguage?: string;
    facetConfiguration?: {
        facetsToShowWhenUncollapsed: number;
        showCount: boolean;
        updateAvailableFacetsWhenFilterChange: boolean;
        facetPanelDefaultOpen: boolean;
    };
}

export const useKendraSearch = ({
    initialQuery,
    userLanguage,
    facetConfiguration,
}: UseKendraSearchProps) => {
    const [dataReady, setDataReady] = useState(false);
    const [searchResults, setSearchResults] = useState<Kendra.QueryResult>({});
    const [docResults, setDocResults] = useState<Kendra.QueryResultItemList>(
        []
    );
    const [currentPageNumber, setCurrentPageNumber] = useState(1);
    const [queryText, setQueryText] = useState(initialQuery || '');
    const [error, setError] = useState<Error | undefined>(undefined);
    const [attributeTypeLookup, setAttributeTypeLookup] = useState<
        IndexFieldNameToDocumentAttributeValueType | undefined
    >(undefined);
    const [availableFacets, setAvailableFacets] = useState(
        AvailableFacetManager.empty()
    );
    const [selectedFacets, setSelectedFacets] = useState(
        SelectedFacetManager.empty()
    );
    const [index, setIndex] = useState<
        Kendra.DescribeIndexResponse | undefined
    >(undefined);
    const [facetsOpen, setFacetsOpen] = useState(
        (facetConfiguration && facetConfiguration.facetPanelDefaultOpen) ??
            false
    );
    const [suggestionsEnabled, setSuggestionsEnabled] = useState(false);
    const [dataSourceNameLookup, setDataSourceNameLookup] = useState<
        DataSourceNameLookup | undefined
    >(undefined);
    const [maxPageWithResults, setMaxPageWithResults] = useState(1);
    const [totalDocumentCount, setTotalDocumentCount] = useState(0);

    const queryApi = useApi({ url: '/kendra/query', method: 'POST' });
    const suggestionsApi = useApi({
        url: '/kendra/suggestions',
        method: 'GET',
    });
    const indexApi = useApi({ url: '/kendra/index', method: 'GET' });
    const suggestionsConfigApi = useApi({
        url: '/kendra/suggestions-config',
        method: 'GET',
    });
    const dataSourcesApi = useApi({
        url: '/kendra/data-sources',
        method: 'GET',
    });

    useEffect(() => {
        indexApi.makeRequest().then((response) => {
            setAttributeTypeLookup(getAttributeTypeLookup(response?.data));
            setIndex(response?.data);
        });
    }, []);

    useEffect(() => {
        if (!index) return;

        dataSourcesApi.makeRequest().then((response) => {
            setDataSourceNameLookup(getDataSourceNameLookup(response?.data));
        });

        suggestionsConfigApi.makeRequest().then((response) => {
            setSuggestionsEnabled(
                response?.data
                    ? response.data.Mode === QuerySuggestionsMode.ENABLED
                    : false
            );
        });
    }, [index]);

    useEffect(() => {
        if (initialQuery && attributeTypeLookup) {
            getResults(initialQuery, 1);
        }
    }, [initialQuery, attributeTypeLookup]);

    const getResultsHelper = (
        queryText: string,
        pageNumber: number,
        filter?: Kendra.AttributeFilter
    ) => {
        setDataReady(false);

        let combinedFilter = filter;
        if (userLanguage) {
            const languageMap: { [key: string]: string } = {
                'English (US)': 'English',
                'French (Canada)': 'French',
                'French (France)': 'French',
                German: 'German',
                Italian: 'Italian',
                Japanese: 'Japanese',
                Polish: 'Polish',
                Spanish: 'Spanish',
                Czech: 'Czech',
                Arabic: 'Arabic',
            };

            const kendraLanguage = languageMap[userLanguage] || userLanguage;

            const languageFilter: Kendra.AttributeFilter = {
                ContainsAny: {
                    Key: 'languages',
                    Value: { StringListValue: [kendraLanguage] },
                },
            };

            if (filter) {
                combinedFilter = {
                    AndAllFilters: [filter, languageFilter],
                };
            } else {
                combinedFilter = languageFilter;
            }
        }

        queryApi
            .makeRequest({
                data: {
                    queryText,
                    pageNumber,
                    attributeFilter: combinedFilter,
                },
            })
            .then((response) => {
                const results = response?.data;
                const documentResults: Kendra.QueryResultItemList = [];
                if (results && results.ResultItems) {
                    results.ResultItems.forEach(
                        (result: Kendra.QueryResultItem) => {
                            switch (result.Type) {
                                case QueryResultType.Document:
                                    documentResults.push(result);
                                    break;
                                default:
                                    break;
                            }
                        }
                    );

                    if (
                        !filter ||
                        (filter &&
                            facetConfiguration?.updateAvailableFacetsWhenFilterChange)
                    ) {
                        setAvailableFacets(
                            AvailableFacetManager.fromQueryResult(results)
                        );
                    }

                    const maxResults = Math.min(
                        results.TotalNumberOfResults || 0,
                        100
                    );
                    const maxPages = Math.ceil(maxResults / PAGE_SIZE);

                    setMaxPageWithResults(maxPages);
                    setTotalDocumentCount(maxResults);
                    setSearchResults(results);
                    setDataReady(true);
                    setDocResults(documentResults);
                    setError(undefined);
                } else {
                    setSearchResults({});
                    setDocResults(documentResults);
                    setDataReady(true);
                    setError(undefined);
                }

                setCurrentPageNumber(pageNumber);
                setQueryText(queryText);
            })
            .catch((e) => {
                setSearchResults({});
                setDataReady(true);
                setError(e);
            });
    };

    const getResults = (queryText: string, pageNumber: number = 1) => {
        setSelectedFacets(selectedFacets.clearAll());
        setMaxPageWithResults(1);
        setTotalDocumentCount(0);
        getResultsHelper(queryText, pageNumber);
    };

    const computeFilterAndReSubmitQuery = (
        queryText: string,
        pageNumber: number = 1
    ) => {
        const filter = selectedFacets.buildAttributeFilter(attributeTypeLookup);
        getResultsHelper(queryText, pageNumber, filter);
    };

    const getQuerySuggestions = async (
        queryText: string
    ): Promise<Kendra.SuggestionList | undefined> => {
        const response = await suggestionsApi.makeRequest({
            params: { queryText },
        });
        return response?.data;
    };

    const onSelectedFacetsChanged = (
        updatedSelectedFacets: SelectedFacetManager
    ) => {
        setSelectedFacets(updatedSelectedFacets);
        computeFilterAndReSubmitQuery(queryText);
    };

    const handleClickExpander = () => {
        setFacetsOpen(!facetsOpen);
    };

    const handlePageChange = (queryText: string, pageNumber: number) => {
        const filter = selectedFacets.buildAttributeFilter(attributeTypeLookup);
        getResultsHelper(queryText, pageNumber, filter);
    };

    return {
        // State
        dataReady,
        searchResults,
        docResults,
        currentPageNumber,
        queryText,
        error,
        attributeTypeLookup,
        availableFacets,
        selectedFacets,
        index,
        facetsOpen,
        suggestionsEnabled,
        dataSourceNameLookup,
        maxPageWithResults,
        totalDocumentCount,
        // Actions
        getResults,
        getQuerySuggestions,
        onSelectedFacetsChanged,
        handleClickExpander,
        handlePageChange,
        setSelectedFacets,
    };
};
